import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'
import { usePrintStore, PrintSettings } from '@renderer/stores/printStore'
import { Button } from '@renderer/components'
import { PrintUtils, buildPrintOptions } from '@renderer/utils/printUtils'
import { logPrintEvent } from '@renderer/utils/printEventLogger'
import { remoteMachineDisplayLabel } from '@renderer/utils/remotePrinterRegistry'

interface RemotePrintSettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * A test card the operator can identify at the printer: names the target
 * machine/printer and the send time, and carries corner markers so margins and
 * position-shift values can be judged from the physical print. Rendered at the
 * page's aspect ratio so scaling behaves like a real job.
 */
function buildRemoteTestImage(
  printerName: string,
  machineLabel: string,
  pageSize: { width: number; height: number }
): string {
  const landscapeAspect = pageSize.width >= pageSize.height
  const w = landscapeAspect ? 1200 : 800
  const h = landscapeAspect ? 800 : 1200
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  ctx.strokeStyle = '#000000'
  ctx.lineWidth = 4
  ctx.strokeRect(10, 10, w - 20, h - 20)

  // Corner markers for margin/shift calibration
  ctx.lineWidth = 6
  const m = 40
  for (const [cx, cy] of [
    [m, m],
    [w - m, m],
    [m, h - m],
    [w - m, h - m]
  ]) {
    ctx.beginPath()
    ctx.moveTo(cx - 25, cy)
    ctx.lineTo(cx + 25, cy)
    ctx.moveTo(cx, cy - 25)
    ctx.lineTo(cx, cy + 25)
    ctx.stroke()
  }

  ctx.fillStyle = '#000000'
  ctx.textAlign = 'center'
  ctx.font = 'bold 56px sans-serif'
  ctx.fillText('REMOTE TEST PRINT', w / 2, h / 2 - 80)
  ctx.font = '36px sans-serif'
  ctx.fillText(printerName, w / 2, h / 2)
  ctx.fillText(machineLabel, w / 2, h / 2 + 56)
  ctx.font = '28px monospace'
  ctx.fillText(new Date().toLocaleString(), w / 2, h / 2 + 128)

  return canvas.toDataURL('image/jpeg', 0.92)
}

const fallbackSettings: PrintSettings = {
  orientation: 'landscape',
  pageSize: { width: 156100, height: 105000 },
  margins: { marginType: 'default', top: 0, bottom: 0, left: 0, right: 0 },
  dpi: { horizontal: 600, vertical: 600 },
  scaleFactor: 100,
  printBackground: true,
  printResolutionScale: 1,
  printPositionShift: { top: 0, bottom: 0, left: 0, right: 0 }
}

/**
 * Detailed print-settings editor for a selected REMOTE printer, usable in the web app
 * (no Electron / local hardware required). Saving stores a per-session override that
 * PrintUtils.sendToPrinter prefers over the booth's registered calibration. "Use booth
 * defaults" clears the override. No main-process changes are involved — the values ride
 * along in the existing /print payload.
 */
export const RemotePrintSettingsModal: React.FC<RemotePrintSettingsModalProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation()
  const { getSelectedRemotePrinter, remotePrintOverride, setRemotePrintOverride } = usePrintStore()
  const selectedRemote = getSelectedRemotePrinter()
  const [settings, setSettings] = useState<PrintSettings>(fallbackSettings)
  const [isTestPrinting, setIsTestPrinting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setSettings(remotePrintOverride || selectedRemote?.printSettings || fallbackSettings)
    }
    // Initialise only when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen) return null

  const marginType = settings.margins?.marginType || 'default'
  const dpi = settings.dpi || { horizontal: 600, vertical: 600 }
  const shift = settings.printPositionShift || {}

  const update = <K extends keyof PrintSettings>(key: K, value: PrintSettings[K]): void => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = (): void => {
    setRemotePrintOverride(settings)
    onClose()
  }

  const handleUseBoothDefaults = (): void => {
    setRemotePrintOverride(undefined)
    onClose()
  }

  /**
   * Test print through the SAME remote path a live session uses, with the
   * modal's UNSAVED settings (that is what "test before you save" means —
   * `remoteSettings` carries them for this call only). A labelled card comes
   * out of the physical printer, so the operator can prove end-to-end that
   * THIS device reaches THAT machine before a paying guest does.
   */
  const handleTestPrint = async (): Promise<void> => {
    const target = getSelectedRemotePrinter()
    if (!target) {
      cogoToast.error(t('remotePrintSettings.noPrinter', 'Select a remote printer first.'))
      return
    }
    setIsTestPrinting(true)
    try {
      const machineLabel = remoteMachineDisplayLabel(target)
      const image = buildRemoteTestImage(target.printerName, machineLabel, settings.pageSize)
      if (!image) {
        cogoToast.error(t('remotePrintSettings.testFailed', 'Test print failed'))
        return
      }
      const result = await PrintUtils.sendToPrinter(
        image,
        buildPrintOptions(target.printerName, settings, {
          copies: 1,
          silent: true,
          remoteSettings: settings,
          // Unique per click, so a quick "did not come out, try again" is not
          // eaten by the duplicate guard.
          duplicateCheckId: `remote-test-${Date.now()}`
        })
      )
      void logPrintEvent({
        eventType: 'test_print',
        copies: 1,
        printerName: result.printedOn?.printerName ?? target.printerName,
        printerTarget: 'remote',
        remotePrinterMachineId: result.printedOn?.machineIdentifier ?? target.machineIdentifier,
        success: result.success,
        ...(result.success ? {} : { error: result.failureReason || result.error })
      })
      if (result.success) {
        cogoToast.success(
          t('remotePrintSettings.testSent', 'Test print sent to {{printer}} ({{machine}})', {
            printer: target.printerName,
            machine: machineLabel
          })
        )
      } else {
        cogoToast.error(
          `${t('remotePrintSettings.testFailed', 'Test print failed')}: ${
            result.failureReason || result.error || 'Unknown error'
          }`
        )
      }
    } finally {
      setIsTestPrinting(false)
    }
  }

  const numberInputClass = 'w-full rounded-md border border-gray-300 p-2 text-sm'
  const labelClass = 'mb-1 block text-xs text-gray-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold">
            {t('remotePrintSettings.title', 'Remote Print Settings')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {selectedRemote
              ? t(
                  'remotePrintSettings.subtitle',
                  "Override the booth's print settings for prints sent from this device."
                )
              : t('remotePrintSettings.noPrinter', 'Select a remote printer first.')}
          </p>
          {selectedRemote && (
            <p className="mt-1 text-sm font-medium text-gray-700">
              {selectedRemote.printerName} — {remoteMachineDisplayLabel(selectedRemote)}
            </p>
          )}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Orientation */}
          <div>
            <label className={labelClass}>{t('printerSettings.orientation', 'Orientation')}</label>
            <select
              title={t('printerSettings.orientation', 'Orientation')}
              value={settings.orientation}
              onChange={(e) => update('orientation', e.target.value as 'landscape' | 'portrait')}
              className={numberInputClass}
            >
              <option value="landscape">{t('printerSettings.landscape', 'Landscape')}</option>
              <option value="portrait">{t('printerSettings.portrait', 'Portrait')}</option>
            </select>
          </div>

          {/* Page size */}
          <div>
            <label className={labelClass}>
              {t('printerSettings.pageSize', 'Page Size (microns)')}
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                aria-label={t('printerSettings.width', 'Width')}
                placeholder={t('printerSettings.width', 'Width')}
                value={settings.pageSize.width}
                onChange={(e) =>
                  update('pageSize', { ...settings.pageSize, width: Number(e.target.value) })
                }
                className={numberInputClass}
              />
              <input
                type="number"
                aria-label={t('printerSettings.height', 'Height')}
                placeholder={t('printerSettings.height', 'Height')}
                value={settings.pageSize.height}
                onChange={(e) =>
                  update('pageSize', { ...settings.pageSize, height: Number(e.target.value) })
                }
                className={numberInputClass}
              />
            </div>
          </div>

          {/* Margins */}
          <div>
            <label className={labelClass}>{t('printerSettings.margins', 'Margins')}</label>
            <select
              title={t('printerSettings.margins', 'Margins Type')}
              value={marginType}
              onChange={(e) =>
                update('margins', {
                  ...(settings.margins || {}),
                  marginType: e.target.value as 'default' | 'none' | 'printableArea' | 'custom'
                })
              }
              className={numberInputClass}
            >
              <option value="default">{t('printerSettings.margins.default', 'Default')}</option>
              <option value="none">{t('printerSettings.margins.none', 'None')}</option>
              <option value="printableArea">
                {t('printerSettings.margins.printableArea', 'Printable Area')}
              </option>
              <option value="custom">{t('printerSettings.margins.custom', 'Custom')}</option>
            </select>
            {marginType === 'custom' && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                {(['top', 'bottom', 'left', 'right'] as const).map((side) => (
                  <div key={side}>
                    <label className={labelClass}>{t(`printerSettings.margins.${side}`, side)}</label>
                    <input
                      type="number"
                      aria-label={side}
                      value={settings.margins?.[side] ?? 0}
                      onChange={(e) =>
                        update('margins', {
                          ...(settings.margins || { marginType: 'custom' }),
                          marginType: 'custom',
                          [side]: Number(e.target.value)
                        })
                      }
                      className={numberInputClass}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* DPI */}
          <div>
            <label className={labelClass}>{t('printerSettings.dpi', 'DPI')}</label>
            <div className="flex gap-2">
              <input
                type="number"
                aria-label={t('printerSettings.dpi.horizontal', 'Horizontal')}
                placeholder={t('printerSettings.dpi.horizontal', 'Horizontal')}
                value={dpi.horizontal}
                onChange={(e) => update('dpi', { ...dpi, horizontal: Number(e.target.value) })}
                className={numberInputClass}
              />
              <input
                type="number"
                aria-label={t('printerSettings.dpi.vertical', 'Vertical')}
                placeholder={t('printerSettings.dpi.vertical', 'Vertical')}
                value={dpi.vertical}
                onChange={(e) => update('dpi', { ...dpi, vertical: Number(e.target.value) })}
                className={numberInputClass}
              />
            </div>
          </div>

          {/* Scale factor */}
          <div>
            <label className={labelClass}>{t('settings.printer.scaleFactor', 'Scale Factor')}</label>
            <input
              type="number"
              min={10}
              max={200}
              aria-label={t('settings.printer.scaleFactor', 'Scale Factor')}
              value={settings.scaleFactor ?? 100}
              onChange={(e) => update('scaleFactor', Number(e.target.value))}
              className={numberInputClass}
            />
          </div>

          {/* Print position shift */}
          <div>
            <label className={labelClass}>
              {t('printerSettings.printPositionShift', 'Print Position Shift (cm)')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['top', 'bottom', 'left', 'right'] as const).map((side) => (
                <div key={side}>
                  <label className={labelClass}>{t(`printerSettings.margins.${side}`, side)}</label>
                  <input
                    type="number"
                    step="0.1"
                    aria-label={`position-shift-${side}`}
                    value={shift[side] ?? 0}
                    onChange={(e) =>
                      update('printPositionShift', {
                        ...shift,
                        [side]: Number(e.target.value)
                      })
                    }
                    className={numberInputClass}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Print background */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="remote-print-background"
              checked={settings.printBackground ?? true}
              onChange={(e) => update('printBackground', e.target.checked)}
            />
            <label htmlFor="remote-print-background" className="text-sm text-gray-700">
              {t('settings.printer.printBackground', 'Print Background')}
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-5 py-4">
          <button
            type="button"
            onClick={handleUseBoothDefaults}
            className="text-sm text-blue-600 hover:underline"
          >
            {t('remotePrintSettings.useBoothDefaults', 'Use booth defaults')}
          </button>
          <div className="flex gap-2">
            <Button
              text={
                isTestPrinting
                  ? t('remotePrintSettings.testPrinting', 'Sending…')
                  : t('remotePrintSettings.testPrint', 'Test Print')
              }
              variant="secondary"
              onClick={() => void handleTestPrint()}
              className={`!px-3 !py-2 ${!selectedRemote || isTestPrinting ? 'opacity-50 pointer-events-none' : ''}`}
            />
            <Button text={t('common.cancel', 'Cancel')} onClick={onClose} className="!px-3 !py-2" />
            <Button
              text={t('common.save', 'Save')}
              onClick={handleSave}
              className="!px-3 !py-2"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default RemotePrintSettingsModal
